---
title: "How to convert PDF to JPG locally in the browser"
description: "Need PDF pages as images? Convert PDF to JPG in the browser at 72, 150 or 300 DPI, without uploading the file to a server."
locale: "en"
date: "2026-06-03"
tags: ["pdf", "jpg", "local"]
relatedTools: ["vJpg"]
translations:
  ru: "kak-konvertirovat-pdf-v-jpg-lokalno"
faq:
  - q: "Is the PDF uploaded to a server?"
    a: "No. The PDF is rendered in the browser with pdf.js, and pages are encoded as JPG on your device. Network tab should not show an upload request with the file."
  - q: "Which DPI should I choose?"
    a: "72 DPI is fine for previews and chat, 150 DPI is a good default for websites and email, 300 DPI is for print or small text. Higher DPI means heavier JPG files."
  - q: "What happens with a multi-page PDF?"
    a: "One page becomes one `.jpg`. If the PDF has several pages, the result is downloaded as a ZIP archive with names like `document-page-01.jpg`."
  - q: "Why can conversion fail on an old phone?"
    a: "Each page is rendered into a canvas. Large PDFs at 300 DPI can hit browser memory limits, especially on older iPhones. Lower the DPI or use a laptop."
---

import { PdfPageImagesForm } from '@/features/pdf-page-images'

`scan_contract_12_pages.pdf` has to go into a chat as images. Not a cloud
link, not a PDF attachment, just page images that open on a phone without
explaining anything. Installing Acrobat for that feels silly, and many
online converters want the file uploaded first.

The form below is `PDF → JPG`. It runs locally: the browser opens the PDF,
renders pages with pdf.js, and gives back JPG files. One page downloads as
one image. Several pages download as a ZIP. The input limit is 100 MB.

<div className="my-8 rounded-xl border bg-card p-5 shadow-sm">
  <PdfPageImagesForm />
</div>

## DPI without the ritual

A PDF is not one big picture. It may contain selectable text, vector
lines, scans, logos, forms. When you make JPG, all of that has to be
drawn at a specific size. That is where DPI enters the room.

72 DPI is the quick preview mode. The page is light, but small text gets
soft after zooming. 150 DPI is the normal middle ground for a website,
email, chat, product card or instruction sheet. 300 DPI is the heavy mode
for print and tiny labels. If the PDF has 40 pages, 300 DPI can turn the
result into a very large ZIP.

I would start with 150 DPI. If you only need to show an invoice, contract
page or menu, it is usually enough. If someone will print a page with
small tables, 300 DPI makes sense. If you just need one image for
Telegram, 72 DPI may be better than trying to send a half-gigabyte archive.

## Why local processing is realistic here

PDF to JPG does not need LibreOffice, server Ghostscript or a worker
queue. Browsers can draw into canvas, and pdf.js can read PDF pages. This
is one of the jobs where local processing is not a privacy sticker glued
onto a server workflow.

Open DevTools, go to Network, drop a PDF into the form and check it. You
will see JS, CSS and the pdf.js worker. The PDF itself is not uploaded.
Smallpdf, iLovePDF, PDF24 and similar tools often solve the same job with
a server upload. For a public brochure, who cares. For a passport scan,
contract or internal instruction, the difference is less academic.

## When JPG is the wrong output

JPG strips PDF down to pixels. Text is no longer selectable, links do not
click, transparency and vector sharpness become a raster image. If you
need an archive document or a file for a lawyer, keep the PDF. If you need
an image for a site, chat, CRM or preview, JPG is the right shape.

Scans deserve one more warning. If the source PDF is already made from
bad phone photos, conversion will not improve them. It will only redraw
what is there. A crooked page stays crooked, gray paper stays gray. Fix
the scan first, then pick the format.
